import { PageHeader } from "@/common/components/page-header";
import { RelatedLinks } from "@/common/components/related-links";
import { Tabs, TabsContent } from "@/common/components/ui/tabs";

import { useParams } from "@tanstack/react-router";
import { useUrlView } from "@/common/hooks/use-url-view";

import { ResponsiveTabTriggerList } from "@/common/components/responsive-tab-trigger-list";
import { DatasetTable } from "./holdings-table";
import { DEFAULT_HOLDINGS_VIEW, HOLDINGS_VIEWS } from "./search";
import {
  holdingsStatement,
  holdingsStatementByAccount,
  holdingsStatementByCurrency,
  holdingsStatementByCostCurrency,
} from "./holdings-statement";
import { createLedgerId } from "@/common/lib/utils/encode";
import { useTranslations } from "@/common/hooks/use-translations";
import {
  holdingsRowsFilter,
  defaultRowsFilter,
  unitsFirstRowsFilter,
} from "./utils";
import { useLedger } from "@/common/hooks/use-ledger";

const LedgerHoldingsTabs = () => {
  const { t } = useTranslations();
  const { ledgerOwner, ledgerName } = useParams({
    from: "/ledger/$ledgerOwner/$ledgerName/holdings",
  });
  const ledgerId = createLedgerId(ledgerOwner, ledgerName);
  // The grouping lives in the URL so drilling into an account and coming back
  // restores the tab; the default grouping is written out too.
  const [selectedTab, setSelectedTab] = useUrlView(
    "/ledger/$ledgerOwner/$ledgerName/holdings",
    HOLDINGS_VIEWS,
    DEFAULT_HOLDINGS_VIEW,
    "view",
    { writeFallback: true },
  );
  const tabOptions = [
    { label: t("page.holdings.holdings"), value: "holdings" },
    { label: t("page.holdings.holdingsByAccount"), value: "by-account" },
    { label: t("page.holdings.holdingsByCurrency"), value: "by-currency" },
    {
      label: t("page.holdings.holdingsByCostCurrency"),
      value: "by-cost-currency",
    },
  ];
  return (
    <Tabs
      className="w-full max-w-full"
      value={selectedTab}
      onValueChange={setSelectedTab}
    >
      <div className="flex items-center justify-between overflow-x-auto">
        <ResponsiveTabTriggerList
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          tabOptions={tabOptions}
        />
      </div>
      <TabsContent value="holdings" className="mt-2 max-w-full">
        <DatasetTable
          query={holdingsStatement}
          ariaLabel={t("page.holdings.holdings")}
          ledgerId={ledgerId}
          rowsFilter={holdingsRowsFilter}
        />
      </TabsContent>

      <TabsContent value="by-account" className="mt-2 max-w-full">
        <DatasetTable
          query={holdingsStatementByAccount}
          ariaLabel={t("page.holdings.holdingsByAccount")}
          ledgerId={ledgerId}
          rowsFilter={defaultRowsFilter}
        />
      </TabsContent>

      <TabsContent value="by-currency" className="mt-2 max-w-full">
        <DatasetTable
          query={holdingsStatementByCurrency}
          ariaLabel={t("page.holdings.holdingsByCurrency")}
          ledgerId={ledgerId}
          rowsFilter={unitsFirstRowsFilter}
        />
      </TabsContent>

      <TabsContent value="by-cost-currency" className="mt-2 max-w-full">
        <DatasetTable
          query={holdingsStatementByCostCurrency}
          ariaLabel={t("page.holdings.holdingsByCostCurrency")}
          ledgerId={ledgerId}
          rowsFilter={unitsFirstRowsFilter}
        />
      </TabsContent>
    </Tabs>
  );
};

const LedgerHoldingsPage = () => {
  const { t } = useTranslations();
  const { ledgerOwner, ledgerName } = useParams({
    from: "/ledger/$ledgerOwner/$ledgerName/holdings",
  });
  const { ledgerName: ledgerDisplayName } = useLedger();

  return (
    <div className="space-y-4 sm:space-y-6 max-w-full">
      <PageHeader
        title={t("page.holdings.holdings")}
        description={t("common.pageDescription.holdings", {
          ledgerName: ledgerDisplayName ?? ledgerName,
        })}
      />
      <LedgerHoldingsTabs />

      <RelatedLinks
        links={[
          {
            label: t("common.relatedLinks.commodities"),
            to: `/ledger/${ledgerOwner}/${ledgerName}/commodities`,
          },
          {
            label: t("common.relatedLinks.overview"),
            to: `/ledger/${ledgerOwner}/${ledgerName}`,
          },
          {
            label: t("common.relatedLinks.statistics"),
            to: `/ledger/${ledgerOwner}/${ledgerName}/statistics`,
          },
        ]}
      />
    </div>
  );
};

export default LedgerHoldingsPage;
